import { ChangeDetectionStrategy, Component, computed, OnInit, signal, inject } from '@angular/core';
import {Router} from '@angular/router';
import {PaymentService} from '../../../services/payment.service';
import {MemberStateService} from '../../../services/member-state.service';
import {FormatService} from '../../../services/format.service';
import {UserContextService} from '../../../services/user-context.service';
import {Observable, Subject} from 'rxjs';
import {map, startWith, switchMap, take} from 'rxjs/operators';
import {AsyncPipe, DatePipe} from '@angular/common';
import {RoutingStateService} from '../../../services/routing-state.service';
import {startOfYear, startOfDay} from 'date-fns';
import {StorageService} from '../../../services/storage.service';
import {DatePickerOptions} from '../../../model/date-picker-options';
import {PaymentMoment} from '../../../model/payment-moment';
import {Member} from '../../../model/member';
import {PaymentsOverview} from '../../../model/payments-overview';
import {ViewState, toViewState} from '../../../model/view-state';
import { FilterComponent } from '../../shared/filter/filter.component';
import { DateFilterComponent } from '../../shared/filter/date-filter/date-filter.component';
import { DropdownFilterComponent } from '../../shared/filter/dropdown-filter/dropdown-filter.component';
import { LoaderComponent } from '../../shared/loader/loader.component';
import { AlertComponent } from '../../shared/alert/alert.component';
import { AllowancesOverviewListByDateComponent } from './allowances-overview-list-by-date/allowances-overview-list-by-date.component';
import { AllowancesOverviewListByMonthComponent } from './allowances-overview-list-by-month/allowances-overview-list-by-month.component';
import { PaginationComponent } from '../../shared/pagination/pagination.component';
import { TranslatePipe } from '@ngx-translate/core';

interface AllowancesData {
  paymentMoments: PaymentMoment[];
  listOfYear: string[];
}

@Component({
    selector: 'myabvv-allowances-overview',
    standalone: true,
    templateUrl: './allowances-overview.component.html',
    imports: [
      AsyncPipe, FilterComponent, DateFilterComponent, DropdownFilterComponent, LoaderComponent, AlertComponent,
      AllowancesOverviewListByDateComponent, AllowancesOverviewListByMonthComponent, PaginationComponent, TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AllowancesOverviewComponent implements OnInit {
  private router = inject(Router);
  private routingState = inject(RoutingStateService);
  private paymentService = inject(PaymentService);
  private memberStateService = inject(MemberStateService);
  private formatService = inject(FormatService);
  private datePipe = inject(DatePipe);
  private storage = inject(StorageService);
  private userContext = inject(UserContextService);


  VIEW_DATE = 'DATE';
  VIEW_MONTH = 'MONTH';
  selectedView: string;
  activeYear = signal('');
  allPaymentMoments = signal<PaymentMoment[]>([]);
  paymentsOverviewOfSpecificYear = computed(() => {
    const year = this.activeYear();
    const moments = this.allPaymentMoments();
    if (!year || moments.length === 0) return [];
    return this.showByDate()
      ? moments.filter(pm => pm.dateOfPayment.toString().substring(0, 4) === year)
      : moments.filter(pm => pm.unemploymentMonth.toString().substring(0, 4) === year);
  });
  sortedData = computed(() => {
    const primary = this.showByMonth() ? 'unemploymentMonth' : 'dateOfPayment';
    const secondary = this.showByMonth() ? 'dateOfPayment' : 'unemploymentMonth';
    return [...this.paymentsOverviewOfSpecificYear()].sort((a, b) =>
      b[primary].localeCompare(a[primary]) || b[secondary].localeCompare(a[secondary]));
  });
  datePickerOptionsStartDate: DatePickerOptions;
  datepickerOptionsEndDate: DatePickerOptions;
  startDate?: Date;
  endDate?: Date;
  today = new Date();
  showByDate = signal(false);
  showByMonth = signal(false);
  viewsToSort: {id: string; label: string; show: boolean}[] = [];

  state$: Observable<ViewState<AllowancesData>>;
  private fetchTrigger$ = new Subject<void>();

  ngOnInit() {
    this.initializeView();
    this.initializeDates();
    this.setCorrectDatePickerOptions();

    this.state$ = this.fetchTrigger$.pipe(
      startWith(void 0),
      switchMap(() => this.buildPaymentsSource$()),
      map((po: PaymentsOverview) => {
        const paymentMoments = po.paymentMoments || [];
        const listOfYear = this.createUniqueListOfYears(paymentMoments);
        const localStorageYear = this.storage.getSelectedYearAllowances();
        this.allPaymentMoments.set(paymentMoments);
        this.activeYear.set(
          localStorageYear && listOfYear.includes(localStorageYear) ? localStorageYear : listOfYear[0] || ''
        );
        return {paymentMoments, listOfYear};
      }),
      toViewState()
    );

    this.viewsToSort = [
      {
        id: 'DATE',
        label: 'ALLOWANCES.OVERVIEW.date_payment',
        show: this.showByDate()
      },
      {
        id: 'MONTH',
        label: 'ALLOWANCES.OVERVIEW.month_unemployment',
        show: this.showByMonth()
      }
    ];
  }

  onGoToDetail(paymentEventDetails: PaymentMoment) {
    this.storeSelectedYear();
    if ( this.showByMonth() ) {
      this.router.navigate(
        ['/unemployment/payments/detailByUnemploymentMonth'],
        {queryParams: {unemploymentMonth: paymentEventDetails.unemploymentMonth}}
      ).then().catch();
    } else {
      this.router.navigate(
        ['/unemployment/payments/detailByPaymentDate'],
        {queryParams: {paymentDate: paymentEventDetails.dateOfPayment}}
      ).then().catch();
    }
  }

  onChangeStartDate(startDate: Date) {
    this.startDate = startDate;

    if (this.startDate > this.endDate) {
      this.endDate = this.startDate;
    }
  }

  onChangeEndDate(endDate: Date) {
    this.endDate = endDate;

    if (this.endDate < this.startDate) {
      this.startDate = this.endDate;
    }
  }

  onChangeViewEvent(event: string) {
    this.selectedView = event;
    this.initializeDates();
    this.setCorrectDatePickerOptions();
  }

  onApplyFilter() {
    this.showByDate.set(this.selectedView === this.VIEW_DATE);
    this.showByMonth.set(this.selectedView === this.VIEW_MONTH);

    this.removeSelectedYearInStore();
    this.storeDates();
    this.storeView();

    this.fetchTrigger$.next();
  }

  onGetPaymentOverviewOfYear(year: string) {
    this.activeYear.set(year);
  }

  onReinitializeFilters() {
    this.removeSelectedYearInStore();
    this.initializeDefaultDates();
    this.onApplyFilter();
  }

  initializeView() {
    const storedView = this.storage.getSelectedAllowancesView();
    if (storedView && this.routingState.getPreviousUrl().includes('detail')) {
      this.selectedView = storedView;
    } else {
      this.selectedView = this.VIEW_MONTH;
    }
    this.showByDate.set(this.selectedView === this.VIEW_DATE);
    this.showByMonth.set(this.selectedView === this.VIEW_MONTH);
  }

  initializeDefaultDates() {
    this.startDate = startOfYear(new Date());
    this.endDate = startOfDay(new Date());
    this.storeDates();
  }

  setCorrectDatePickerOptions() {
    if (this.selectedView === this.VIEW_DATE) {
      this.initializeEndDateDatePickerOptionsDayView();
      this.initializeStartDateDatePickerOptionsDayView();
    } else {
      this.initializeStartDateDatePickerOptionsMonthView();
      this.initializeEndDateDatePickerOptionsMonthView();
    }
  }

  initializeDates() {
    this.endDate = this.storage.getEndDate()
      ? startOfDay(new Date(this.formatService.parseDate(this.storage.getEndDate())))
      : startOfDay(new Date());
    this.startDate = this.storage.getStartDate() ? new Date(this.storage.getStartDate()) : startOfYear(new Date());
    this.storeDates();
  }


  initializeEndDateDatePickerOptionsDayView() {
    this.datepickerOptionsEndDate = {
      startView: 'date',
      format: 'dd-mm-yy',
      endDate: this.today,
    };
  }


  initializeEndDateDatePickerOptionsMonthView() {
    this.datepickerOptionsEndDate = {
      format: 'mm-yy',
      endDate: this.today,
      startView: 'month',
    };
  }

  initializeStartDateDatePickerOptionsDayView() {
    this.datePickerOptionsStartDate = {
    format: 'dd-mm-yy',
    endDate: this.today,
    startView: 'date',
  };
}

initializeStartDateDatePickerOptionsMonthView() {
    this.datePickerOptionsStartDate = {
      format: 'mm-yy',
      endDate: this.today,
      startView: 'month',
    };
  }

  hasData() {
    return this.paymentsOverviewOfSpecificYear().length > 0;
  }

  private buildPaymentsSource$(): Observable<PaymentsOverview> {
    let formattedStart: string;
    let formattedEnd: string;

    if (this.showByMonth()) {
      formattedStart = this.datePipe.transform(this.startDate, 'yyyy-MM');
      formattedEnd = this.datePipe.transform(this.endDate, 'yyyy-MM');
    } else {
      formattedStart = this.datePipe.transform(this.startDate, 'yyyy-MM-dd');
      formattedEnd = this.datePipe.transform(this.endDate, 'yyyy-MM-dd');
    }

    const isInternalUser = this.userContext.isInternalUser();

    if (isInternalUser) {
      return this.memberStateService.getMember().pipe(
        take(1),
        switchMap((m: Member) =>
          this.showByMonth()
            ? this.paymentService.getPaymentsOverviewByMonth(m.personalInformation.inss, formattedStart, formattedEnd)
            : this.paymentService.getPaymentsOverviewByDate(m.personalInformation.inss, formattedStart, formattedEnd)
        )
      );
    }

    return this.showByMonth()
      ? this.paymentService.getMyPaymentsOverviewByMonth(formattedStart, formattedEnd)
      : this.paymentService.getMyPaymentsOverviewByDate(formattedStart, formattedEnd);
  }

  private createUniqueListOfYears(paymentMoments: PaymentMoment[]): string[] {
    const years: string[] = [];
    paymentMoments.forEach(pm => {
      const dateField = this.showByDate() ? pm.dateOfPayment : pm.unemploymentMonth;
      const year = this.datePipe.transform(dateField, 'yyyy');
      if (!years.includes(year)) {
        years.push(year);
      }
    });
    years.sort((a, b) => Number(b) - Number(a));
    return years;
  }

  private storeDates() {
    // Same coercion sessionStorage.setItem applied implicitly (undefined -> 'undefined')
    this.storage.setStartDate(String(this.startDate));
    this.storage.setEndDate(String(this.endDate));
  }

  private storeView() {
    this.storage.setSelectedAllowancesView(this.selectedView);
  }

  private storeSelectedYear() {
    this.storage.setSelectedYearAllowances(this.activeYear());
  }

  private removeSelectedYearInStore() {
    this.storage.removeSelectedYearAllowances();
  }
}
